import { usePersistentState } from '@hooks/usePersistentState';
import { appliedSkip } from './JourneyView';

/** Plain titles for the resupply IVR's steps, as a business user would describe them. */
const GLANCE_TITLES = {
  'answer-detection': 'Checks whether a person, voicemail or fax answers',
  greeting: 'Greeting',
  'language-offer': 'Offers Spanish (press 9)',
  'verify-dob': 'Asks for the birth year',
  consent: 'Asks whether they want to order',
  'education-statement': 'Insurance education statement',
  'pre-supply-question': 'Questions before the supplies',
  'order-all-offer': 'Reads the eligible supplies and offers to order all of them',
  'supply-item': 'Asks about each eligible item',
  'confirm-supplies': 'Reads the order back',
  'supply-period': '90 or 30 days',
  'optional-question': 'Optional questions',
  'address-confirm': 'Reads the address on file back',
  disclaimer: 'Disclaimer, then the order is placed',
};

/** Steps the call only reaches on a side path; listed once under "Only if…" instead of in the main order. */
const SIDE_STEPS = ['no-consent', 'address-record', 'address-playback', 'disclaimer-cancel', 'give-up', 'voicemail', 'voicemail-override'];
const SUCCESS_END = 'order-created';

/** The questions a step asks (expanded question groups), with the ones turned off marked. */
function questionsOf(step) {
  return step.prompts.filter((p) => p.group).map((p) => ({ label: p.label, on: p.enabled }));
}

function GlanceRow({ num, step, ctx, preview, onJump }) {
  const skipped = appliedSkip(step);
  const off = !step.enabled;
  const questions = questionsOf(step);
  const why = off ? `turned off for ${ctx.name}: ${step.offNote}`
    : skipped ? `skipped${preview ? ` for ${preview.name}` : ''}: ${skipped.guardReason || skipped.label}` : null;
  const effects = [...new Set((step.answerEffects || []).map((e) => e.text))];
  return (
    <li className={`cx-glance-row${why ? ' cx-glance-skipped' : ''}`}>
      <button type="button" className="cx-link" onClick={() => onJump(step.id)}>
        <span className="cx-glance-num">{num}.</span> {GLANCE_TITLES[step.id] || step.title}
      </button>
      {why && <span className="cx-muted cx-small"> ({why})</span>}
      {!why && questions.length > 0 && (
        <div className="cx-small">
          {questions.map((q, i) => (
            <span key={q.label} className={q.on ? '' : 'cx-muted'}>
              {i > 0 && ' · '}{q.label}{q.on ? '' : ' (off)'}
            </span>
          ))}
        </div>
      )}
      {!why && questions.length === 0 && step.repeat?.over === 'eligibleItems' && preview && (
        <div className="cx-small">{preview.eligibleItems.length ? preview.eligibleItems.join(' · ') : 'nothing eligible'}</div>
      )}
      {!why && effects.length > 0 && <div className="cx-small cx-muted">⚑ {effects.join(' · ')}</div>}
    </li>
  );
}

/**
 * Call at a glance: the resupply call's main path in one short numbered list, with the steps skipped for
 * the company (or the previewed patient) greyed out and the reason, then the side paths and early endings.
 * Clicking a step opens it in the full script.
 */
export default function CallAtAGlance({ journey, ctx, onJump }) {
  const [open, setOpen] = usePersistentState('cx.glance', true, { storage: 'local' });
  if (journey.id !== 'resupply-ivr') return null;

  const preview = journey.preview;
  const steps = journey.steps.filter((s) => s.group !== 'system');
  const main = steps.filter((s) => !s.outcome && !SIDE_STEPS.includes(s.id));
  const side = steps.filter((s) => SIDE_STEPS.includes(s.id));
  const success = steps.find((s) => s.id === SUCCESS_END);
  const earlyEnds = steps.filter((s) => s.outcome && s.id !== SUCCESS_END);

  return (
    <div className="cx-glance">
      <div className="cx-row">
        <h3 className="cx-section-title">Call at a glance{preview ? ` · ${preview.name}` : ''}</h3>
        <span className="cx-spacer" />
        <button type="button" className="cx-btn cx-btn-small" onClick={() => setOpen(!open)} aria-expanded={open}>{open ? 'Hide' : 'Show'}</button>
      </div>
      {open && (
        <>
          <ol className="cx-glance-list">
            {main.map((step, i) => <GlanceRow key={step.id} num={i + 1} step={step} ctx={ctx} preview={preview} onJump={onJump} />)}
            {success && (
              <li className="cx-glance-row">
                <button type="button" className="cx-link" onClick={() => onJump(success.id)}>
                  <span className="cx-glance-num">✓</span> {success.title}
                </button>
                <span className="cx-muted cx-small"> ({success.outcome})</span>
              </li>
            )}
          </ol>
          <div className="cx-small">
            <b>Only if…</b>{' '}
            {side.map((s, i) => (
              <span key={s.id}>{i > 0 && ' · '}<button type="button" className="cx-link" onClick={() => onJump(s.id)}>{s.title}</button></span>
            ))}
          </div>
          <div className="cx-small cx-mt">
            <b>Ways the call can end early:</b>{' '}
            {earlyEnds.map((s, i) => (
              <span key={s.id}>{i > 0 && ' · '}<button type="button" className="cx-link" onClick={() => onJump(s.id)}>{s.title}</button></span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
